import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { joiResolver } from '@hookform/resolvers/joi';

import { useDebounceCallback } from 'usehooks-ts';

import { useAppContext } from '../../../../../../app/Context';
import { i18nNS } from '../../../../../../i18n';
import { FormValues } from './types';
import { validationSchema } from './validators';
import { getTimerMetaData, setTimerMetaData } from '../../../../../../db/timer';

const TIMER_SAVE_DEBOUNCE_TIME = 300;

const useTitle = () => {
  const { t } = useTranslation([i18nNS.TIMER, i18nNS.COMMON]);

  const { presentationId, activeSlideId, setSlideTitleAndSubtitle } = useAppContext();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      title: '',
    },
    mode: 'all',
  });

  const { handleSubmit, setValue } = formMethods;

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!presentationId) return;
    if (!activeSlideId) return;

    setTimerMetaData(presentationId, activeSlideId, { title: data.title });

    const response = getTimerMetaData(presentationId, activeSlideId);
    if (!response) return;
    const { countdownFrom = 0 } = response;

    const minutes = `${Math.floor(countdownFrom / 60)}`.padStart(2, '0');
    const seconds = `${countdownFrom % 60}`.padStart(2, '0');

    setSlideTitleAndSubtitle({
      title: t('acadly_activity_plugin', { ns: i18nNS.COMMON }),
      subtitle: t('title_timer_ran_for_minutes_count_minutes_seconds_count_seconds', {
        ns: i18nNS.TIMER,
        title: data.title ?? t('untitled_timer', { ns: i18nNS.TIMER }),
        minutesCount: minutes,
        secondsCount: seconds,
      }),
    });
  };

  const onChange = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleChange = useDebounceCallback(onChange, TIMER_SAVE_DEBOUNCE_TIME);

  useEffect(
    function setInitialValues() {
      if (!presentationId) return;
      if (!activeSlideId) return;
      const response = getTimerMetaData(presentationId, activeSlideId);
      if (!response) return;
      const { title = '' } = response;
      setValue('title', title);
    },
    [presentationId, activeSlideId, setValue]
  );

  return { formMethods, onSubmit, handleChange };
};

export default useTitle;
